import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import type { IslandStep } from '../../components/StepIsland/StepIsland';
import { StepHint } from '../../components/StepHint/StepHint';
import { useConfirm } from '../../components/confirm';
import { guardedExit, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { fmtLocalShort } from '../../utils/datetime';
import { useAuthStore } from '../../auth/store';
import { useNavPermission } from '../../hooks/useNavPermission';
import { NotesListPopup, useNotesCount } from './SdmsProjectNotes';
import { TrendIcon, TrendChartPopup, resolveRoutes, type TrendRoute, type TrendRow } from '../trend';
import { QualifierCombo, type QualifierOption } from './QualifierCombo';
import type { ResultEntryJob } from './ResultEntryExpressPage';
import type { ReeFilters } from './ResultEntryQueryPanel';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
// Stage 3 (Permit-To-Notification) — shared permit-limit parse + stacked/red cell.
import { parsePermitLimits, permitAlertText, permitResultValue, permitRowClassFor, type PermitLimit } from './permitLimit';
import { usePermitRowColors } from './permitRowColors';
import { renderPermitAlertCell, renderPermitLimitCell } from './permitLimitCell';
import { permitLimitAutoFit, PERMIT_LIMIT_WIDTH } from './permitLimitWidth';
import { RESULT_GRID_NO_ZEBRA } from './resultGrids';

// Legacy "Selection Mode" (ResultEntryQueryPanelWebViewController) — Sample shows
// the real samples, QC Sample the QC controls; both FILTER the grid.
// Result tabs (task RTCFR, 2026-09-08): Sample / QC by isQc; Surrogate / Internal
// Standard by the Test-setup parameter flags. MUTUALLY EXCLUSIVE — every row belongs
// to exactly ONE tab (a surrogate/ISTD parameter is pulled out of Sample/QC).
type SelMode = 'Sample' | 'QC Sample' | 'Surrogate' | 'Internal Standard';
const RESULT_TABS: readonly SelMode[] = ['Sample', 'QC Sample', 'Surrogate', 'Internal Standard'];
const resultTabOf = (r: { isSurrogate?: boolean; isInternalStandard?: boolean; isQc?: boolean }): SelMode =>
  r.isSurrogate ? 'Surrogate'
    : r.isInternalStandard ? 'Internal Standard'
    : r.isQc === true ? 'QC Sample'
    : 'Sample';

// QC-column fields dropped on the Surrogate / Internal Standard tabs (user request
// 2026-09-09). Surrogate keeps %Rec (percentRec) + Rec Ctrl Limit (lowRec) + D.F. (df) —
// recovery is the surrogate metric; Internal Standard drops those too, leaving only
// result-entry-relevant columns.
const SURR_DROP = new Set(['rl', 'ucl', 'mdl', 'lowStd', 'highStd', 'percentRpd', 'lowRpd']);
const ISTD_DROP = new Set(['df', 'rl', 'ucl', 'mdl', 'lowStd', 'highStd', 'percentRpd', 'percentRec', 'lowRpd', 'lowRec']);

// datetime-local seed = "now" in YYYY-MM-DDTHH:mm (local wall-clock, 24-hour).
function nowLocalInput(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ── Reporting-result derivation (ported from legacy ResultEntryViewController) ──
// Round to `sig` significant figures, preserving trailing zeros — legacy
// Roundoff.RoundoffInput (e.g. sigRound(32,3) = "32.0", sigRound(0.12345,3) = "0.123").
function sigRound(value: number, sig: number): string {
  if (!isFinite(value) || sig <= 0) return String(value);
  if (value === 0) return (0).toFixed(Math.max(0, sig - 1));
  const order = Math.ceil(Math.log10(Math.abs(value)));
  const power = sig - order;
  const mag = Math.pow(10, power);
  const rounded = Math.round(value * mag) / mag;
  return power > 0 ? rounded.toFixed(power) : String(rounded);
}

interface ResultInputs {
  rl?: string | null; defaultResult?: string | null;
  sigFig?: string | null; decimalPlaces?: string | null; cutOff?: string | null;
}

/**
 * Faithful port of the legacy Result derivation (ResultEntryViewController
 * ~line 1656): value >= RL → rounded (SigFig via RoundoffInput, or Decimal via
 * FormatDecimalValue, CutOff-gated); value < RL (or rounding drops below) →
 * DefaultResult ("BRL"); RL empty → the value as-is. Empty input clears Result.
 */
function deriveResult(numStr: string, t: ResultInputs): string {
  const raw = (numStr ?? '').replace(/[<>]/g, '').trim();
  if (raw === '') return '';                       // cleared → clear Result
  const numeric = parseFloat(raw);
  if (!isFinite(numeric)) return raw;              // non-numeric text → mirror
  const rlStr = (t.rl ?? '').replace(/[^.0-9]/g, '');
  if (rlStr === '') return raw;                    // no RL → mirror the value
  const rl = parseFloat(rlStr);
  const sig = (t.sigFig ?? '').trim();
  const dec = (t.decimalPlaces ?? '').trim();
  const cut = (t.cutOff ?? '').trim();

  let result: string;
  if (numeric >= rl) {
    if (cut !== '') {
      const cutV = parseFloat(cut);
      if (sig !== '' && numeric >= cutV) result = sigRound(numeric, parseInt(sig, 10));
      else if (dec !== '' && numeric < cutV) result = numeric.toFixed(parseInt(dec, 10));
      else result = raw;
    } else if (sig !== '' && sig !== '0') {
      result = sigRound(numeric, parseInt(sig, 10));
    } else if (dec !== '') {
      result = numeric.toFixed(parseInt(dec, 10));
    } else {
      result = raw;
    }
  } else {
    result = raw;
  }
  const resultNum = parseFloat(result);
  // Below the reporting limit → the test's DefaultResult (e.g. "ND"), blank when the
  // test has none — legacy manual entry (ResultEntryViewController ~L1776-1778).
  if (isFinite(resultNum) && resultNum < rl) return (t.defaultResult ?? '').trim();
  return result;
}

// A calculated cell (%Rec, %RPD, RL) that can't compute yet because an upstream
// data-source value is missing (owner request 2026-08-01): show the value if
// present, else a small WARNING SYMBOL only — the reason ("Spike Amount is empty
// — %Rec calculation pending.") appears on HOVER, never as text in the cell
// (owner refinement 2026-08-01: message text made the grid noisy; the symbol
// flags the row at a glance and the tooltip explains it on demand). Blank when
// the engine reports no note (nothing to compute yet).
// Also used on the SOURCE cells (SpikeAmount, Dup source) so the warning sits
// where the fix belongs — those can carry a value AND a warning at once (a
// linked duplicate whose own result isn't entered yet), hence value + symbol.
function pendingCell(value: string | null, note: string | null): ReactNode {
  if (!note) return value ?? '';
  const warn = (
    <i className="ti ti-alert-triangle ref-pending-note" title={note} role="img" aria-label={note} />
  );
  return value ? <span className="ref-pending-wrap">{value}{warn}</span> : warn;
}

// mirror ResultEntrySampleRow in the API
interface SampleRow {
  id:            string;
  jobID:         string | null;
  jobOid:        string | null;
  qcBatchID:     string | null;
  uqabid:        string | null;   // analytical-batch Oid — scopes the shared Trend popup; null = Trend disabled
  sysSampleCode: string | null;
  sampleId:      string | null;   // linked sample code (QC/Surrogate/ISTD tabs); null for non-sample QC
  sampleName:    string | null;
  groupTest:     string | null;
  test:          string | null;
  method:        string | null;
  parameter:     string | null;
  numericResult: string | null;
  result:        string | null;
  rawData:       string | null;
  units:         string | null;
  df:            string | null;
  qualifier:     string | null;
  volume:        string | null;
  rl:            string | null;
  ucl:           string | null;   // UCLLimit — Blank QC control limit (Testparameter.HighCLimit); QC tab, Blank rows only
  defaultResult: string | null;
  sigFig:        string | null;
  decimalPlaces: string | null;
  cutOff:        string | null;
  lowStd:        string | null;
  highStd:       string | null;
  // QC-control-only fields (null for real samples) — shown in the QC Sample tab.
  qcType:          string | null;
  spikeAmount:     string | null;
  spikeAmountUnit: string | null;
  percentRec:      string | null;
  percentRpd:      string | null;
  lowRec:          string | null;
  highRec:         string | null;
  lowRpd:          string | null;
  highRpd:         string | null;
  // "Why is this still blank?" — set by the engine only once the analyst has
  // entered their own result but an upstream data-source value is missing
  // (owner request 2026-08-01). Shown as muted "pending" text, never a hard error.
  percentRecNote:  string | null;
  percentRpdNote:  string | null;
  rlNote:          string | null;
  // The same warning on the SOURCE cell that's actually missing (owner request
  // 2026-08-01) — always set together with the two calculated notes above.
  spikeAmountNote: string | null;
  dupSourceNote:   string | null;
  receivedDate:  string | null;
  analyzedDate:  string | null;
  analyzedBy:    string | null;
  statusText:    string | null;
  statusId:      string | null;
  matrix:        string | null;
  component:     string | null;
  comment:       string | null;
  isFieldTest:   boolean;
  statusCode:    number | null;
  isQc:          boolean;
  isSurrogate:        boolean;   // Testparameter.Surroagate — Surrogate tab
  isInternalStandard: boolean;   // Testparameter.InternalStandard — Internal Standard tab
  // Formula-engine inputs (2026-07-28): exact Test+Matrix+Method key + QC source links.
  testMethodOid: string | null;
  dupSource:     string | null;
  spikeSource:   string | null;
  // New fields (Excel Result Entry Express_G6 spec, 2026-08-28)
  siteName:         string | null;
  finalResult:      string | null;
  finalResultUnits: string | null;
  mdl:              string | null;
  qcStatus:         string | null;
  enteredDate:      string | null;
  enteredBy:        string | null;
  client:           string | null;
  projectID:        string | null;
  dateCollected:    string | null;
  moisture:         string | null;
  dryWeight:        string | null;
  sampleAmount:     string | null;
  sampleVol:        string | null;
  standardVol:      string | null;
  area:             string | null;
  permitLimit:      string | null;
  permitType:       string | null;
  permitStatus:     string | null;
  alert:            string | null;
  // Stage 3 (Permit-To-Notification): the row's applicable permit limits as a raw JSON
  // array from the reader (null when no permit), parsed once on load into `permitLimits`
  // for the stacked/red Permit Limit cell (permitLimit.ts).
  permitLimitsJson?: string | null;
  permitLimits?:    PermitLimit[];
  dupSourceResult:  string | null;
  spikeSourceResult: string | null;
}

/**
 * Result Entry Express — DETAIL. Loads a job's pending-entry sample-parameter
 * rows and lets the analyst type results in a pure grid (in-place editing),
 * then Save / Submit. Ported from legacy XAF Result Entry Express.
 *
 * PHASE 1: read + full editing UI. Save / Submit persist through the write path
 * which is GATED to a non-prod DB until approved (owner decision 2026-07-16) —
 * the buttons are present and collect the edits; wiring the POST is Phase 2.
 */
export interface ResultEntryDetailHandle {
  save(): void;
  submit(): void;
}

type DetailProps = {
  /** null = no job selected yet; renders the empty grid shell */
  job: ResultEntryJob | null;
  /** Multiple jobs (QC batch multi-job mode) — when provided, all jobs' samples
   *  are loaded together into one grid. `job` is null in this mode. */
  jobs?: ResultEntryJob[];
  /** QC Batch IDs that were active when the query ran — when provided in multi-job
   *  mode the fetched rows are filtered to only those matching these batch IDs,
   *  preventing rows from other batches on the same jobs from appearing. */
  batchIds?: string[];
  /** Row-level filters from the query panel — applied after fetching so only
   *  rows matching the analyst's criteria (test, matrix, analyzed dates) appear.
   *  Job-level filters (client, project, received dates) are already applied by
   *  the job query and don't need re-filtering here. */
  rowFilters?: Pick<ReeFilters, 'test' | 'matrix' | 'sampleLocation' | 'analyzedFrom' | 'analyzedTo'>;
  onBack: () => void;
  kind?: 'job' | 'batch';
  inline?: boolean;
  onLoadMs?: (ms: number | null) => void;
  onStepChange?: (s: { resultsDone: boolean; savedOnce: boolean; canSave: boolean; canSubmit: boolean }) => void;
  /** (inline) Publishes the active grid's export payload so the HOST page can
   *  render the "Export to" button in its top control bar (user request 2026-09-05,
   *  Export moved between Refresh and Query). */
  onExportData?: (d: { filename: string; headers: string[]; rows: string[][] }) => void;
};

// Map a REE grid row → the shared Trend router's TrendRow (task RTCFR, 2026-09-10).
// Reuses the SAME routing the SDMS Result Validation grid uses — REE does NOT
// re-implement any trend logic. Fields REE doesn't carry (parameterOid / qcType Oid /
// qc role chain) are left null; the router degrades gracefully (Sample → site/project
// trend, Surrogate / Internal Standard → their recovery/response series).
function reeToTrendRow(r: SampleRow): TrendRow {
  return {
    id:                 r.id,
    sys:                r.sysSampleCode || null,
    parameter:          r.parameter || null,
    siteName:           r.siteName || null,
    projectName:        r.projectID || null,
    qcTypeName:         r.qcType || null,
    parentSys:          r.sampleId || null,   // sample-linked QC → its parent sample code
    testMethodOid:      r.testMethodOid || null,
    isInternalStandard: r.isInternalStandard ?? false,
    isSurrogate:        r.isSurrogate ?? false,
    analyzedDate:        r.analyzedDate ?? null,
  };
}

const ResultEntryDetail = React.forwardRef<ResultEntryDetailHandle, DetailProps>(
function ResultEntryDetail({ job, jobs, batchIds, rowFilters, onBack, kind = 'job', inline = false, onLoadMs, onStepChange, onExportData }, ref) {
  usePermitRowColors();   // Settings ▸ Permit Row Colors — re-colour the rows when the lab's rules load
  const { confirm, alert } = useConfirm();
  // Every Result Entry message shows as the shared centered POPUP (modal), never a
  // top banner (user request 2026-07-17) — the tone drives the icon/colour.
  const say = useCallback(
    (message: string, tone: 'success' | 'info' | 'warning' | 'error' = 'info') => {
      void alert({ title: 'Result Entry Express', message, tone });
    }, [alert]);
  const meName = useAuthStore(s => s.user?.name || s.user?.email || s.user?.id || '');
  const [rows, setRows] = useState<SampleRow[]>([]);
  // Shared Trend wiring (task RTCFR, 2026-09-10) — same router + popup as SDMS Result
  // Validation. The route per row is resolved once from the loaded rows; the popup is
  // scoped to that row's analytical batch (uqabid), exactly like the SDMS grid.
  const trendRouteMap = useMemo(() => resolveRoutes(rows.map(reeToTrendRow)), [rows]);
  const [openTrend, setOpenTrend] = useState<
    { route: TrendRoute; label: string; batchOid: string; method: string | null } | null
  >(null);
  const [loading, setLoading] = useState(job != null); // false when no job yet
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  // Sidebar nav-away, Sign Out, Back/breadcrumb and tab-close all ask
  // "Unsaved changes will be lost. Do you want to proceed?" while dirty.
  useUnsavedGuard(dirty);
  const [search, setSearch] = useState('');
  // Row selection · selection-mode label · notes/comment popups.
  const [selMode, setSelMode] = useState<SelMode>('Sample');
  const [selRows, setSelRows] = useState<Set<string | number>>(new Set());
  const [notesOpen, setNotesOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  // Project-note count for THIS job/batch (shared SdmsProjectNotes — auto-refreshes).
  const notesCount = useNotesCount(job?.jobOid ?? '', job?.jobOid ?? '');
  // Qualifier lookup (dbo.Qualifiers) for the Qualifier checkbox-combo.
  const [qualifiers, setQualifiers] = useState<QualifierOption[]>([]);
  // Employee display names for the AnalyzedBy combo.
  const [analysts, setAnalysts] = useState<string[]>([]);
  // Inline maximize — overlays the grid as a fixed full-screen panel.
  const [maximized, setMaximized] = useState(false);
  // Current user's rights on THIS form, resolved by its own navigation item via the
  // shared per-form permission hook (legacy resolves permission per NavigationId).
  // canWrite → Save/Submit + cell editing + note authoring; canDelete → Delete.
  // Read is always on. Fail-closed until resolved, so buttons never flash enabled.
  const perm = useNavPermission('Result Entry');
  const canWrite = perm.canWrite;
  const canDelete = perm.canDelete;
  // Right-click "Copy to…" context menu on an editable cell.
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; field: keyof SampleRow; value: string } | null>(null);

  useEffect(() => {
    let alive = true;
    api.get('/api/v1/result-entry/qualifiers', { silentLoading: true } as never)
      .then(r => { if (alive) setQualifiers((r.data as { data: QualifierOption[] }).data ?? []); })
      .catch(() => { /* combo just shows an empty list */ });
    api.get('/api/v1/result-entry/analysts', { silentLoading: true } as never)
      .then(r => { if (alive) setAnalysts((r.data as { data: string[] }).data ?? []); })
      .catch(() => { /* combo just shows an empty list */ });
    return () => { alive = false; };
  }, []);

  // ── TABLE-DRIVEN calculation (2026-07-28) ────────────────────────────────
  // Posts the grid rows to the Result Entry formula engine, which resolves each
  // row's pipeline from Settings → SDMS → Result Entry Formula (exact
  // Test+Matrix+Method match) and returns the computed TargetFields (%Rec, %RPD,
  // Result, …). Rows with no mapping come back untouched — the local
  // deriveResult fallback still covers them. Silent (no loader flash).
  const recomputeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const recompute = useCallback((current: SampleRow[]) => {
    const payload = {
      rows: current.map(r => ({
        id: r.id, testMethodOid: r.testMethodOid, sysSampleCode: r.sysSampleCode,
        parameter: r.parameter, qcType: r.qcType, isQc: r.isQc,
        numericResult: r.numericResult, result: r.result, df: r.df, rl: r.rl,
        spikeAmount: r.spikeAmount, dupSource: r.dupSource, spikeSource: r.spikeSource,
        sigFig: r.sigFig, decimalPlaces: r.decimalPlaces, cutOff: r.cutOff,
        defaultResult: r.defaultResult,
        lowRec: r.lowRec, highRec: r.highRec, lowRpd: r.lowRpd, highRpd: r.highRpd,
      })),
    };
    api.post('/api/v1/result-entry-formula/compute', payload, { silentLoading: true } as never)
      .then(res => {
        const data = (res.data as { data: Record<string, {
          result?: string | null; percentRec?: string | null; percentRpd?: string | null;
          percentRecNote?: string | null; percentRpdNote?: string | null; rlNote?: string | null;
          spikeAmountNote?: string | null; dupSourceNote?: string | null;
        }> }).data ?? {};
        if (Object.keys(data).length === 0) return;
        setRows(prev => prev.map(r => {
          const c = data[r.id];
          if (!c) return r;
          return {
            ...r,
            // %Rec / %RPD are ENGINE-OWNED display columns, so the engine's answer
            // is taken VERBATIM — including null. Previously these fell back to the
            // previous value (`?? r.percentRec`), which left a STALE number on
            // screen after the analyst cleared the NumericResult it was derived
            // from (owner report 2026-08-01: "I remove the numeric result but the
            // RPD still show old value"). Result stays `?? r.result` because it is
            // an ANALYST-EDITABLE cell — the engine may only overwrite it when it
            // actually computed something, never blank out a typed-in value.
            percentRec: c.percentRec ?? null,
            percentRpd: c.percentRpd ?? null,
            // Engine Result wins for MAPPED combos (table-driven); unmapped rows
            // keep the local deriveResult value.
            result: c.result ?? r.result,
            // "Pending" notes — cleared when the engine no longer reports one
            // (e.g. the analyst just filled in the missing source value).
            percentRecNote: c.percentRecNote ?? null,
            percentRpdNote: c.percentRpdNote ?? null,
            rlNote: c.rlNote ?? null,
            spikeAmountNote: c.spikeAmountNote ?? null,
            dupSourceNote: c.dupSourceNote ?? null,
          };
        }));
      })
      .catch(() => { /* engine unavailable → local derive stays */ });
  }, []);

  const load = useCallback(async () => {
    // Build the effective job list: multi-job mode (jobs prop) takes priority,
    // then single-job mode (job prop); empty → stay in empty-shell state.
    const jobList = jobs && jobs.length > 0 ? jobs : (job ? [job] : []);
    if (jobList.length === 0) { setLoading(false); return; }
    setLoading(true); setError(null);
    const t0 = performance.now();
    // Pre-build filter sets for O(1) lookups. Trim to guard against whitespace
    // differences between the filter value and the DB value.
    const batchSet  = batchIds && batchIds.length > 0
      ? new Set(batchIds.map(b => b.trim()))
      : null;
    const testSet   = rowFilters && rowFilters.test.length   > 0
      ? new Set(rowFilters.test.map(v => v.toLowerCase().trim()))
      : null;
    const matrixSet = rowFilters && rowFilters.matrix.length > 0
      ? new Set(rowFilters.matrix.map(v => v.toLowerCase().trim()))
      : null;
    const siteSet   = rowFilters && rowFilters.sampleLocation.length > 0
      ? new Set(rowFilters.sampleLocation.map(v => v.toLowerCase().trim()))
      : null;
    const anaFrom   = rowFilters?.analyzedFrom || null;   // 'YYYY-MM-DD' or ''
    const anaTo     = rowFilters?.analyzedTo   || null;
    try {
      // Fetch all jobs in parallel (multi-job QC batch) or the one job.
      const chunks = await Promise.all(
        jobList.map(j =>
          api.get(`/api/v1/result-entry/${j.jobOid}/samples`,
            { params: kind === 'batch' ? { kind: 'batch' } : undefined })
            .then(res => {
              const data = (res.data as { data: SampleRow[] }).data ?? [];
              // Apply row-level filters: batch IDs, then test/matrix/analyzed dates.
              // Job-level filters (client, project, received dates) were already
              // applied by the job query — no need to re-check them here.
              // Batch filter: regular samples (isQc !== true) always pass through —
              // they have no qcBatchID so filtering them by batch would hide them.
              // Only QC control rows are narrowed to the selected batch.
              let filtered = batchSet
                ? data.filter(r => r.isQc !== true || batchSet.has((r.qcBatchID ?? '').trim()))
                : data;
              if (testSet)
                filtered = filtered.filter(r => testSet.has((r.test ?? '').toLowerCase().trim()));
              if (matrixSet)
                filtered = filtered.filter(r => matrixSet.has((r.matrix ?? '').toLowerCase().trim()));
              if (siteSet)
                filtered = filtered.filter(r => siteSet.has((r.siteName ?? '').toLowerCase().trim()));
              // Date Analyzed range: a pending-entry sample that has NOT been analyzed
              // yet carries no AnalyzedDate — it is exactly what Result Entry Express is
              // for (batched AND unbatched samples awaiting entry, user request
              // 2026-09-10). A NULL-analyzed row must NOT be hidden by the (often
              // auto-filled) analyzed-date window, so it always passes; only rows that
              // DO carry an analyzed date are narrowed to the range.
              if (anaFrom)
                filtered = filtered.filter(r => !r.analyzedDate || r.analyzedDate.slice(0, 10) >= anaFrom);
              if (anaTo)
                filtered = filtered.filter(r => !r.analyzedDate || r.analyzedDate.slice(0, 10) <= anaTo);
              // Stamp every row with its source job's ID and Oid so the grid
              // shows the JobID column correctly, and save/submit can group rows.
              // In BATCH mode the SP provides sc.JobID for real-sample rows
              // (non-null); QC control rows have no Samplelogin so sc is NULL —
              // fall back to j.jobID (the batch code) for those.
              // DF is shown/saved exactly as stored — G5 never defaults it to '1'
              // (G5 parity fix #11, 2026-09-25); the server formula treats a missing
              // DF as 1 for its calculation only, it never writes it.
              return filtered.map(r => ({
                ...r,
                jobID: (r.jobID ?? '').trim() ? r.jobID : j.jobID,
                jobOid: j.jobOid,
              }));
            })
        )
      );
      const allRows = chunks.flat();
      // Deduplicate by SampleParameter Oid: in JOB mode, when multiple jobs belong
      // to the same QC batch, the batch's QC controls appear in every job's result
      // set. Keep the first occurrence so each QC control row appears exactly once.
      const seenIds = new Set<string>();
      const mapped = allRows.filter(r => seenIds.has(r.id) ? false : (seenIds.add(r.id), true));
      // Auto-stamp EnteredDate + EnteredBy for rows that don't already have them.
      // These are read-only audit fields — the analyst cannot edit them; the first
      // person who opens the job for entry is recorded automatically.
      const entryStamp = nowLocalInput();
      // Fill QcBatchID for regular sample rows: the DB returns NULL for non-QC rows
      // (sp.UQABID is NULL for real samples). When the user loaded via a single QC
      // batch, propagate that batch ID to the Sample rows so the column isn't blank.
      const singleBatch = batchIds && batchIds.length === 1 ? batchIds[0] : null;
      const stamped = mapped.map(r => ({
        ...r,
        enteredDate: (r.enteredDate ?? '').trim() ? r.enteredDate : entryStamp,
        enteredBy:   (r.enteredBy   ?? '').trim() ? r.enteredBy   : meName,
        qcBatchID:   (r.qcBatchID   ?? '').trim() ? r.qcBatchID   : (singleBatch ?? r.qcBatchID),
        // Stage 3 — parse the applicable permit limits once on load (undefined = no permit).
        permitLimits: parsePermitLimits(r.permitLimitsJson),
      }));
      setRows(stamped);
      recompute(stamped);   // table-driven %Rec/%RPD on load (stored results present)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load samples');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [job, jobs, batchIds, rowFilters, kind, recompute, meName]);

  useEffect(() => { void load(); }, [load]);

  // When the parent clears the job / jobs (e.g. Clear button), wipe all
  // internal state so the grid goes back to its empty-shell appearance.
  useEffect(() => {
    if (job || (jobs && jobs.length > 0)) return;
    setRows([]);
    setDirty(false);
    setSavedOnce(false);
    setSelRows(new Set());
    setSearch('');
    setError(null);
    setLoadMs(null);
  }, [job, jobs]);

  // After Load, default to the Sample tab (user request 2026-09-08) — a fresh Load
  // (job/jobs becomes active) always opens on Sample Results, regardless of which tab
  // was active before. (The empty-shell reset above returns early when a job is set,
  // so the tab default is handled here.)
  useEffect(() => {
    if (job || (jobs && jobs.length > 0)) setSelMode('Sample');
  }, [job, jobs]);

  // Selection Mode splits real samples (Sample) from QC controls (QC Sample) —
  // legacy parity; then the free-text search narrows within the current mode.
  const filtered = useMemo(() => {
    // Exclusive tab partition (resultTabOf): Surrogate / Internal Standard parameters
    // are pulled out of Sample/QC into their own tab, so no row appears twice. A pre-
    // restart API that omits the flags treats a row as Sample (isQc !== true).
    const byMode = rows.filter(r => resultTabOf(r) === selMode);
    if (!search.trim()) return byMode;
    const t = search.toLowerCase();
    return byMode.filter(r =>
      (r.sysSampleCode ?? '').toLowerCase().includes(t) ||
      (r.test ?? '').toLowerCase().includes(t) ||
      (r.parameter ?? '').toLowerCase().includes(t) ||
      (r.method ?? '').toLowerCase().includes(t));
  }, [rows, search, selMode]);

  // A row is complete once it has a Result — for numeric tests it auto-fills from
  // NumericResult (or the parameter's DefaultResult / BRL when below RL); for P/A
  // tests the analyst sets it directly. Drives the required tint + Submit guard.
  const needsResult = (r: SampleRow) => !(r.result ?? '').trim();
  const pendingCount = useMemo(() => rows.filter(needsResult).length, [rows]);
  const resultsDone = rows.length > 0 && pendingCount === 0;

  // In-place commit → local buffer (never per-keystroke to the DB). Entering a
  // NumericResult on a numeric test auto-derives the Result column (legacy).
  // Apply one field value to a row, deriving Result when NumericResult changes
  // (value / rounded when >= RL; DefaultResult below RL). A typed "<"/">" is
  // stripped before comparing, as legacy does.
  const applyField = (r: SampleRow, field: keyof SampleRow, val: string): SampleRow => {
    const next = { ...r, [field]: val } as SampleRow;
    if (field === 'numericResult') {
      next.result = deriveResult(val, {
        rl: r.rl, defaultResult: r.defaultResult, sigFig: r.sigFig,
        decimalPlaces: r.decimalPlaces, cutOff: r.cutOff,
      });
    }
    return next;
  };

  // Debounced recompute: the formula-engine POST fires 400 ms after the last edit
  // instead of on every individual cell commit, preventing API flooding during
  // rapid data entry (typing a value + tabbing to the next field).
  const debouncedRecompute = useCallback((current: SampleRow[]) => {
    if (recomputeTimer.current) clearTimeout(recomputeTimer.current);
    recomputeTimer.current = setTimeout(() => { recompute(current); }, 400);
  }, [recompute]);

  const commit = useCallback((row: SampleRow, col: AppColumn<SampleRow>, val: string) => {
    setRows(prev => {
      const next = prev.map(r => r.id === row.id ? applyField(r, col.field as keyof SampleRow, val) : r);
      debouncedRecompute(next);   // debounced — fires 400 ms after last edit
      return next;
    });
    setDirty(true);
  }, [debouncedRecompute]);

  // Live-stage every keystroke into the master `rows` buffer (task RTCFR, 2026-09-10:
  // "switching between Sample and QC Sample must not clear entries"). onCellCommit only
  // fires on blur/Enter, so a value still being typed when the analyst clicks another tab
  // would otherwise be dropped (the cell editor unmounts as `filtered` changes). Staging
  // live keeps the typed value in `rows` immediately — it survives the tab switch and is
  // shown when the analyst returns. No recompute here (that stays on commit, debounced),
  // so this is cheap; commit still runs its normal apply+recompute when editing finishes.
  const stageCell = useCallback((row: SampleRow, col: AppColumn<SampleRow>, val: string) => {
    setRows(prev => prev.map(r => r.id === row.id ? applyField(r, col.field as keyof SampleRow, val) : r));
    setDirty(true);
  }, []);

  // Qualifier combo writes straight to the row buffer (its own onChange).
  const setCellValue = useCallback((rowId: string, field: keyof SampleRow, val: string) => {
    setRows(prev => {
      const next = prev.map(r => r.id === rowId ? { ...r, [field]: val } as SampleRow : r);
      debouncedRecompute(next);   // debounced — fires 400 ms after last edit
      return next;
    });
    setDirty(true);
  }, [debouncedRecompute]);

  // Right-click "Copy to…" on an editable cell (Copy To All Cell, legacy parity).
  // Per-column handler factory — the shared grid exposes onCellContextMenu on the
  // COLUMN (e, row) => void.
  const cellCtx = useCallback((field: keyof SampleRow) =>
    (e: ReactMouseEvent<HTMLTableCellElement>, row: SampleRow) => {
      e.preventDefault();
      setCtxMenu({ x: e.clientX, y: e.clientY, field, value: String((row as unknown as Record<string, unknown>)[field] ?? '') });
    }, []);

  // "Copy To All Cell" — copies the value into that column of the SELECTED rows.
  // With no selection it shows a validation popup and copies nothing.
  const copyToAll = useCallback(() => {
    if (!ctxMenu) return;
    const { field, value } = ctxMenu;
    setCtxMenu(null);
    if (selRows.size === 0) { say('Select the rows to copy to first.', 'warning'); return; }
    setRows(prev => {
      const next = prev.map(r => selRows.has(r.id) ? applyField(r, field, value) : r);
      recompute(next);   // immediate (bulk copy — all rows arrive at once)
      return next;
    });
    setDirty(true);
    say(`Copied to ${selRows.size} row${selRows.size === 1 ? '' : 's'}.`, 'success');
  }, [ctxMenu, selRows, say, recompute]);

  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    document.addEventListener('click', close);
    document.addEventListener('scroll', close, true);
    return () => { document.removeEventListener('click', close); document.removeEventListener('scroll', close, true); };
  }, [ctxMenu]);

  // Switching tabs PRESERVES the selection (user request 2026-09-10: "selecting all in
  // the Sample tab must not clear when moving to QC Samples"). selRows is a cross-tab set
  // of row ids; each tab's grid shows the checked state only for its OWN rows (the shared
  // grid's selectedIds lookup), and select-all/toggle build from the existing selection
  // (AppDataGrid toggleAll starts from selectedIds), so a Sample selection stays intact
  // while you work in QC / Surrogate / Internal Standard and Save/Submit act on the union.
  // (Previously an effect cleared selRows on every selMode change — removed.)

  // Selecting a row stamps AnalyzedBy = current user (fills blanks only). AnalyzedDate
  // is NOT auto-stamped — the analyst enters it (legacy ResultEntryViewController
  // ~L3677, Task 29187) — except field tests, which default to the collect date.
  const onSelectionChange = useCallback((ids: Set<string | number>) => {
    const added = [...ids].filter(id => !selRows.has(id));
    if (added.length) {
      const addedSet = new Set(added);
      setRows(rs => rs.map(r => addedSet.has(r.id)
        ? { ...r,
            analyzedBy:   (r.analyzedBy ?? '').trim() || meName,
            analyzedDate: (r.analyzedDate ?? '').trim()
              ? r.analyzedDate
              : (r.isFieldTest && (r.dateCollected ?? '').trim() ? r.dateCollected : r.analyzedDate) }
        : r));
      setDirty(true);
    }
    setSelRows(ids);
  }, [selRows, meName]);

  // Delete: clears the entered result on the selected rows IN THE DATABASE (Result,
  // NumericResult, AnalyzedBy, AnalyzedDate → empty; the test row itself stays) —
  // legacy ResultDelete — then reloads so the grid shows what is stored.
  const deleteSelected = useCallback(async () => {
    if (!canDelete) { say('You do not have Delete permission for Result Entry.', 'warning'); return; }
    if (selRows.size === 0) { say('Select the rows to delete first.', 'warning'); return; }
    const count = selRows.size;
    const ok = await confirm({ title: 'Delete', message: `Clear the result on ${count} selected row${count === 1 ? '' : 's'}?`, tone: 'danger' });
    if (!ok) return;
    try {
      const byOid = new Map<string, string[]>();
      for (const r of rows) {
        if (!selRows.has(r.id)) continue;
        const oid = r.jobOid ?? job?.jobOid ?? '';
        if (!oid) continue;
        if (!byOid.has(oid)) byOid.set(oid, []);
        byOid.get(oid)!.push(r.id);
      }
      let totalDeleted = 0;
      for (const [oid, ids] of byOid.entries()) {
        const res = await api.post(`/api/v1/result-entry/${oid}/delete`, { rows: ids.map(id => ({ id })) });
        totalDeleted += (res.data as { deletedRows: number }).deletedRows;
      }
      setSelRows(new Set());
      say(`Deleted. (${totalDeleted} result${totalDeleted === 1 ? '' : 's'} cleared)`, 'success');
      await load();
    } catch (e: unknown) {
      say(e instanceof Error ? e.message : 'Delete failed.', 'error');
    }
  }, [rows, selRows, job, confirm, say, canDelete, load]);

  // Trend column (task RTCFR, 2026-09-10) — rendered right AFTER RawData on every tab.
  // Reuses the shared <TrendIcon> + <TrendChartPopup> and the shared route resolver
  // (no trend logic duplicated in REE). Enabled only when the row carries an analytical
  // batch (uqabid) to scope the batch-keyed series — the SAME behaviour as the SDMS
  // Result Validation grid, which disables the icon for rows it can't route/scope.
  const trendCol: AppColumn<SampleRow> = {
    field: 'trend', header: 'Trend', width: 70, sortable: false, filterable: false,
    text: () => '',
    render: (r) => (
      <TrendIcon
        route={r.uqabid ? trendRouteMap.get(r.id) : undefined}
        onOpen={() => {
          const route = trendRouteMap.get(r.id);
          if (route?.routable && r.uqabid) {
            setOpenTrend({
              route,
              label: `${r.sysSampleCode ?? ''}${r.parameter ? ` · ${r.parameter}` : ''}`,
              batchOid: r.uqabid,
              method: r.method,
            });
          }
        }}
      />
    ),
  };

  // Sample-tab columns in the order specified by Excel Result Entry Express_G6.xlsx.
  // Editable cells: NumericResult, Result, D.F., Qualifier, Comment.
  // MDL · LOQ · UQL are READ-ONLY (user request 2026-09-22).
  const sampleCols: AppColumn<SampleRow>[] = [
    { field: 'jobID',            header: 'JobID',            width: 120, text: r => r.jobID ?? '' },
    { field: 'qcBatchID',        header: 'QBatchID',         width: 110, text: r => r.qcBatchID ?? '' },
    { field: 'sysSampleCode',    header: 'SampleID',         width: 140, text: r => r.sysSampleCode ?? '' },
    { field: 'sampleName',       header: 'SampleName',       width: 130, text: r => r.sampleName ?? '' },
    { field: 'siteName',         header: 'SiteName',         width: 130, text: r => r.siteName ?? '' },
    { field: 'test',             header: 'Test',             width: 150, text: r => r.test ?? '' },
    { field: 'method',           header: 'Method',           width: 120, text: r => r.method ?? '' },
    { field: 'parameter',        header: 'Parameter',        width: 140, text: r => r.parameter ?? '' },
    { field: 'numericResult',    header: 'NumericResult',    width: 120, text: r => r.numericResult ?? '',
      edit: canWrite ? { type: 'text' } : undefined, onCellContextMenu: canWrite ? cellCtx('numericResult') : undefined },
    { field: 'result',           header: 'Result',           width: 120, text: r => r.result ?? '',
      edit: canWrite ? { type: 'text', requiredEmpty: r => !(r.result ?? '').trim() } : undefined,
      onCellContextMenu: canWrite ? cellCtx('result') : undefined },
    { field: 'units',            header: 'Units',            width: 80,  text: r => r.units ?? '' },
    { field: 'finalResult',      header: 'FinalResult',      width: 120, text: r => r.finalResult ?? '' },
    { field: 'finalResultUnits', header: 'FinalResultUnits', width: 120, text: r => r.finalResultUnits ?? '' },
    { field: 'rawData',          header: 'RawData',          width: 90,  text: r => r.rawData ?? '' },
    trendCol,
    { field: 'df',               header: 'D.F.',             width: 70,  text: r => r.df ?? '',
      edit: canWrite ? { type: 'text' } : undefined, onCellContextMenu: canWrite ? cellCtx('df') : undefined },
    { field: 'moisture',         header: '%Moisture',        width: 80,  text: r => r.moisture ?? '' },
    { field: 'dryWeight',        header: 'DryWeight',        width: 80,  text: r => r.dryWeight ?? '' },
    { field: 'qualifier',        header: 'Qualifier',        width: 130, text: r => r.qualifier ?? '',
      render: canWrite
        ? r => <QualifierCombo value={r.qualifier ?? ''} options={qualifiers}
                 onChange={v => setCellValue(r.id, 'qualifier', v)} />
        : undefined,
      onCellContextMenu: canWrite ? cellCtx('qualifier') : undefined },
    { field: 'sampleAmount',     header: 'SampleAmount',     width: 100, text: r => r.sampleAmount ?? '' },
    { field: 'volume',           header: 'FinalVolume',      width: 80,  text: r => r.volume ?? '' },
    { field: 'sampleVol',        header: 'SampleVol',        width: 80,  text: r => r.sampleVol ?? '' },
    { field: 'standardVol',      header: 'StandardVol',      width: 80,  text: r => r.standardVol ?? '' },
    { field: 'area',             header: 'Area',             width: 70,  text: r => r.area ?? '' },
    // MDL · LOQ · UQL: global/shared per test — READ-ONLY (user request 2026-09-22).
    // Displayed from the test setup; not editable in Result Entry Express.
    { field: 'mdl',              header: 'MDL',              width: 80,  text: r => r.mdl ?? '' },
    { field: 'lowStd',           header: 'LOQ',              width: 80,  text: r => r.lowStd ?? '' },
    { field: 'highStd',          header: 'UQL',              width: 80,  text: r => r.highStd ?? '' },
    { field: 'permitLimit',      header: 'PermitLimit',      width: PERMIT_LIMIT_WIDTH.min,  text: r => r.permitLimit ?? '',
      // Width follows the content (shared rule, RTCFR "Dynamic Permit Limit Column Width").
      autoFit: permitLimitAutoFit<SampleRow>(r => r.permitLimits, r => r.permitLimit ?? ''),
      // Stage 3: stack every applicable permit limit, red when the result is outside it
      // (permitLimit.ts). Falls back to the scalar value when the job has no permit.
      render: r => renderPermitLimitCell(r.permitLimits, permitResultValue(r.result, r.numericResult), r.units, r.permitLimit ?? '') },
    // Alert — "⚑ Notify lab" when a permit limit is violated (shared permitAlertState,
    // RTCFR 2026-10-13); follows the edited result live. Same cell on REE / RT / RV / RA.
    { field: 'alert',            header: 'Alert',            width: 110,
      text: r => permitAlertText(r.permitLimits, r.result, r.numericResult, r.units, r.alert),
      render: r => renderPermitAlertCell(r.permitLimits, r.result, r.numericResult, r.units, r.alert) },
    { field: 'analyzedDate',     header: 'AnalyzedDate',     width: 150, text: r => r.analyzedDate ? fmtLocalShort(r.analyzedDate) : '',
      edit: canWrite ? { type: 'datetime' } : undefined,
      onCellContextMenu: canWrite ? cellCtx('analyzedDate') : undefined },
    // AnalyzedBy — plain text at rest, opens the shared inline combo on click
    // (BO-UI V3 §9 "editor only on edit"; commits via the grid's onCellCommit).
    { field: 'analyzedBy',       header: 'AnalyzedBy',       width: 140, text: r => r.analyzedBy ?? '',
      edit: canWrite ? { type: 'combo', options: analysts, clearable: true } : undefined,
      onCellContextMenu: canWrite ? cellCtx('analyzedBy') : undefined },
    // EnteredDate & EnteredBy are read-only — auto-stamped with the entry session's
    // date/time and the currently logged-in user when the job is first loaded.
    { field: 'enteredDate',      header: 'EnteredDate',      width: 150, text: r => r.enteredDate ? fmtLocalShort(r.enteredDate) : '' },
    { field: 'enteredBy',        header: 'EnteredBy',        width: 120, text: r => r.enteredBy ?? '' },
    { field: 'statusText',       header: 'Status',           width: 140, type: 'status', text: r => r.statusText ?? '' },
    { field: 'statusId',         header: 'StatusID',         width: 80,  text: r => r.statusId ?? '' },
    { field: 'isFieldTest',      header: 'IsFieldTest',      width: 80,  text: r => r.isFieldTest ? 'Yes' : 'No' },
    { field: 'matrix',           header: 'SampleMatrix',     width: 90,  text: r => r.matrix ?? '' },
    { field: 'component',        header: 'Component',        width: 100, text: r => r.component ?? '' },
    { field: 'client',           header: 'Client',           width: 130, text: r => r.client ?? '' },
    { field: 'projectID',        header: 'ProjectID',        width: 90,  text: r => r.projectID ?? '' },
    { field: 'receivedDate',     header: 'DateReceived',     width: 150, text: r => r.receivedDate ? fmtLocalShort(r.receivedDate) : '' },
    { field: 'dateCollected',    header: 'DateCollected',    width: 150, text: r => r.dateCollected ? fmtLocalShort(r.dateCollected) : '' },
    { field: 'comment',          header: 'Comment',          width: 160, text: r => r.comment ?? '',
      edit: canWrite ? { type: 'text' } : undefined, onCellContextMenu: canWrite ? cellCtx('comment') : undefined },
  ];

  // QC Sample tab columns in the order specified by Excel Result Entry Express_G6.xlsx.
  // Editable: NumericResult, Result, Qualifier, Comment. D.F. is read-only for QC.
  // RPDCtrlLimit / RecCtrlLimit combine the low–high limit pairs into one display column.
  const qcCols: AppColumn<SampleRow>[] = [
    { field: 'qcBatchID',        header: 'QBatchID',         width: 130, text: r => r.qcBatchID ?? '' },
    { field: 'jobID',            header: 'JobID',            width: 120, text: r => r.jobID ?? '' },
    { field: 'sysSampleCode',    header: 'SysSampleCode',    width: 150, text: r => r.sysSampleCode ?? '' },
    // Linked parent sample (task RTCFR, 2026-09-10) — QC/Surrogate/ISTD tabs (surr/istd
    // inherit via the qcCols filter); blank for non-sample QC. Relationship-sourced.
    { field: 'sampleId',         header: 'SampleID',         width: 140, text: r => r.sampleId ?? '' },
    { field: 'qcType',           header: 'QCType',           width: 90,  text: r => r.qcType ?? '' },
    { field: 'test',             header: 'Test',             width: 150, text: r => r.test ?? '' },
    { field: 'method',           header: 'Method',           width: 120, text: r => r.method ?? '' },
    { field: 'parameter',        header: 'Parameter',        width: 140, text: r => r.parameter ?? '' },
    { field: 'numericResult',    header: 'NumericResult',    width: 120, text: r => r.numericResult ?? '',
      edit: canWrite ? { type: 'text' } : undefined, onCellContextMenu: canWrite ? cellCtx('numericResult') : undefined },
    { field: 'result',           header: 'Result',           width: 120, text: r => r.result ?? '',
      edit: canWrite ? { type: 'text', requiredEmpty: r => !(r.result ?? '').trim() } : undefined,
      onCellContextMenu: canWrite ? cellCtx('result') : undefined },
    { field: 'units',            header: 'Units',            width: 80,  text: r => r.units ?? '' },
    { field: 'finalResult',      header: 'FinalResult',      width: 120, text: r => r.finalResult ?? '' },
    { field: 'finalResultUnits', header: 'FinalResultUnits', width: 120, text: r => r.finalResultUnits ?? '' },
    { field: 'rawData',          header: 'RawData',          width: 90,  text: r => r.rawData ?? '' },
    trendCol,
    { field: 'df',               header: 'D.F.',             width: 70,  text: r => r.df ?? '' },
    { field: 'rl',               header: 'RL',               width: 70,  text: r => r.rl ?? '',
      render: r => pendingCell(r.rl, r.rlNote) },
    // UCLLimit — Blank QC control limit, immediately after RL (RTCFR Phase 2, 2026-09-15).
    // Populated by the SP only on Blank-type QC rows; blank elsewhere.
    { field: 'ucl',              header: 'UCLLimit',         width: 90,  text: r => r.ucl ?? '' },
    // MDL · LOQ · UQL: global/shared per test — READ-ONLY (user request 2026-09-22).
    // Displayed from the test setup; not editable in Result Entry Express.
    { field: 'mdl',              header: 'MDL',              width: 80,  text: r => r.mdl ?? '' },
    { field: 'lowStd',           header: 'LOQ',              width: 80,  text: r => r.lowStd ?? '' },
    { field: 'highStd',          header: 'UQL',              width: 80,  text: r => r.highStd ?? '' },
    // The QC SOURCE cells carry the same warning symbol as the calculated columns
    // so the analyst sees the gap where the fix belongs (owner request 2026-08-01).
    { field: 'spikeAmount',      header: 'SpikeAmount',      width: 100, text: r => r.spikeAmount ?? '',
      render: r => pendingCell(r.spikeAmount, r.spikeAmountNote) },
    { field: 'spikeAmountUnit',  header: 'SpikeAmountUnit',  width: 120, text: r => r.spikeAmountUnit ?? '' },
    { field: 'percentRpd',       header: '%RPD',             width: 70,  text: r => r.percentRpd ?? '',
      render: r => pendingCell(r.percentRpd, r.percentRpdNote) },
    { field: 'percentRec',       header: '%Rec',             width: 70,  text: r => r.percentRec ?? '',
      render: r => pendingCell(r.percentRec, r.percentRecNote) },
    // Combined control-limit display columns (Just one control limit per Excel spec).
    { field: 'lowRpd',           header: 'RPD Ctrl Limit',   width: 110, text: r => [r.lowRpd, r.highRpd].filter(Boolean).join(' – ') },
    { field: 'lowRec',           header: 'Rec Ctrl Limit',   width: 110, text: r => [r.lowRec, r.highRec].filter(Boolean).join(' – ') },
    { field: 'qualifier',        header: 'Qualifier',        width: 130, text: r => r.qualifier ?? '',
      render: canWrite
        ? r => <QualifierCombo value={r.qualifier ?? ''} options={qualifiers}
                 onChange={v => setCellValue(r.id, 'qualifier', v)} />
        : undefined,
      onCellContextMenu: canWrite ? cellCtx('qualifier') : undefined },
    { field: 'dupSource',        header: 'DupSource',        width: 150, text: r => r.dupSource ?? '',
      render: r => pendingCell(r.dupSource, r.dupSourceNote) },
    { field: 'dupSourceResult',  header: 'DupSourceResult',  width: 130, text: r => r.dupSourceResult ?? '' },
    { field: 'spikeSource',      header: 'SpikeSource',      width: 150, text: r => r.spikeSource ?? '' },
    { field: 'spikeSourceResult',header: 'SpikeSourceResult',width: 130, text: r => r.spikeSourceResult ?? '' },
    { field: 'qcStatus',         header: 'QCStatus',         width: 90,  text: r => r.qcStatus ?? '' },
    { field: 'analyzedDate',     header: 'AnalyzedDate',     width: 150, text: r => r.analyzedDate ? fmtLocalShort(r.analyzedDate) : '',
      edit: canWrite ? { type: 'datetime' } : undefined,
      onCellContextMenu: canWrite ? cellCtx('analyzedDate') : undefined },
    // AnalyzedBy — plain text at rest, opens the shared inline combo on click
    // (BO-UI V3 §9 "editor only on edit"; commits via the grid's onCellCommit).
    { field: 'analyzedBy',       header: 'AnalyzedBy',       width: 140, text: r => r.analyzedBy ?? '',
      edit: canWrite ? { type: 'combo', options: analysts, clearable: true } : undefined,
      onCellContextMenu: canWrite ? cellCtx('analyzedBy') : undefined },
    // EnteredDate & EnteredBy are read-only — auto-stamped on load.
    { field: 'enteredDate',      header: 'EnteredDate',      width: 150, text: r => r.enteredDate ? fmtLocalShort(r.enteredDate) : '' },
    { field: 'enteredBy',        header: 'EnteredBy',        width: 120, text: r => r.enteredBy ?? '' },
    { field: 'statusText',       header: 'Status',           width: 140, type: 'status', text: r => r.statusText ?? '' },
    { field: 'statusId',         header: 'StatusID',         width: 80,  text: r => r.statusId ?? '' },
    { field: 'matrix',           header: 'SampleMatrix',     width: 90,  text: r => r.matrix ?? '' },
    { field: 'comment',          header: 'Comment',          width: 160, text: r => r.comment ?? '',
      edit: canWrite ? { type: 'text' } : undefined, onCellContextMenu: canWrite ? cellCtx('comment') : undefined },
  ];

  // Active tab's column set — used by the grid + export. Sample uses the plain sample
  // columns; QC Sample uses the full QC set. Surrogate / Internal Standard TRIM the QC
  // set (user request 2026-09-09) by filtering out the columns that don't apply — so
  // order / alignment / export stay identical to QC, just fewer columns:
  //   • Surrogate  — drop RL · MDL · LOQ · UQL · %RPD · %Rec · RPD Ctrl Limit
  //                  (keep Rec Ctrl Limit — surrogate recovery limits apply).
  //   • Internal Standard — drop all of the above PLUS D.F. and Rec Ctrl Limit.
  const cols = selMode === 'Sample'            ? sampleCols
             : selMode === 'Surrogate'         ? qcCols.filter(c => !SURR_DROP.has(c.field))
             : selMode === 'Internal Standard' ? qcCols.filter(c => !ISTD_DROP.has(c.field))
             : qcCols;

  // Save and Submit both act on the TICKED rows only (user decision 2026-07-29) —
  // never the whole grid.
  const selectedRows = useMemo(() => rows.filter(r => selRows.has(r.id)), [rows, selRows]);

  // A job is loaded either via the single `job` prop OR the multi-job `jobs`
  // prop (QC-batch mode, e.g. loading by Batch ID bundles several jobs' rows
  // together — `job` stays undefined then). Save/Submit enablement must accept
  // either, never just `!!job` (that wrongly disabled both buttons in batch mode).
  const hasAnyJob = !!job || !!(jobs && jobs.length > 0);

  // A selected row already carrying Status = "Pending Submission" was saved to
  // the DB in a PRIOR session (savedOnce is only ever true after a Save click in
  // THIS session) — so Submit is already valid for it without a redundant
  // re-save. Only when EVERY selected row is already Pending Submission does
  // this unlock Submit directly; the user can still Save (edit) first if they want.
  const selectedAlreadyPendingSubmission = selectedRows.length > 0
    && selectedRows.every(r => (r.statusText ?? '').trim().toLowerCase() === 'pending submission');

  // Save's ONLY validation: rows must be selected. No result/analyzed-date/
  // analyzed-by check here — that gate moved to Submit (below).
  const validateSelection = useCallback(async (verb: string) => {
    if (selRows.size > 0) return false;
    await alert({ title: 'Validation', tone: 'warning', message: `Select the rows to ${verb} first.` });
    return true;
  }, [alert, selRows.size]);

  // Submit's full validation: rows must be selected AND every selected row must
  // have Result + Analyzed Date + Analyzed By (mandatory) — checked only at
  // Submit, scoped to the selected rows, never the whole grid.
  const validateSubmit = useCallback(async () => {
    if (selRows.size === 0) {
      await alert({ title: 'Validation', tone: 'warning', message: 'Select the rows to submit first.' });
      return true;
    }
    const incomplete = selectedRows.filter(r =>
      !(r.result ?? '').trim() || !(r.analyzedDate ?? '').trim() || !(r.analyzedBy ?? '').trim());
    if (incomplete.length > 0) {
      await alert({
        title: 'Validation',
        tone: 'warning',
        message: `Result, Analyzed Date, and Analyzed By are required before submitting — ${incomplete.length} of ${selRows.size} selected ${selRows.size === 1 ? 'row is' : 'rows are'} incomplete.`,
      });
      return true;
    }
    return false;
  }, [alert, selRows, selectedRows]);

  // Phase-2 Save — persist the SELECTED rows to the write DB (non-prod), then
  // reload so the grid reflects the stamped AnalyzedBy/Date from the DB.
  // In multi-job mode the selected rows may span several jobs; we group them by
  // their stamped jobOid and POST one request per job.
  const saveFlow = useCallback(async () => {
    if (!hasAnyJob) return;
    if (!canWrite) { say('You do not have Write permission for Result Entry.', 'warning'); return; }
    if (await validateSelection('save')) return;
    const ok = await confirm({ title: 'Save', message: `Do you want to Save the ${selRows.size} selected result${selRows.size === 1 ? '' : 's'}?` });
    if (!ok) return;
    try {
      // Group selected rows by their jobOid (single-job: all rows share job.jobOid).
      const byOid = new Map<string, typeof selectedRows>();
      for (const r of selectedRows) {
        const oid = r.jobOid ?? job?.jobOid ?? '';
        if (!oid) continue;
        if (!byOid.has(oid)) byOid.set(oid, []);
        byOid.get(oid)!.push(r);
      }
      let totalSaved = 0, totalRequested = 0;
      for (const [oid, jobRows] of byOid.entries()) {
        const payload = { rows: jobRows.map(r => ({
          id: r.id, numericResult: r.numericResult, result: r.result, df: r.df,
          qualifier: r.qualifier, comment: r.comment, analyzedBy: r.analyzedBy, analyzedDate: r.analyzedDate,
        })) };
        const res = await api.post(`/api/v1/result-entry/${oid}/save`, payload);
        const d = res.data as { savedRows: number; requested: number };
        totalSaved += d.savedRows; totalRequested += d.requested;
      }
      setSavedOnce(true); setDirty(false);
      say(totalSaved === totalRequested
        ? `Saved successfully. (${totalSaved} result${totalSaved === 1 ? '' : 's'})`
        : `Saved ${totalSaved} of ${totalRequested}. The rest aren't at Pending Entry in the write DB.`,
        'success');
      await load();
    } catch (e: unknown) {
      say(e instanceof Error ? e.message : 'Save failed.', 'error');
    }
  }, [hasAnyJob, job, jobs, confirm, validateSelection, selectedRows, selRows.size, say, load, canWrite]);

  // Phase-2 Submit — persist the SELECTED rows + advance the workflow status
  // (settings-driven), then return to the list (legacy View.Close parity).
  // Multi-job: group by jobOid and submit per job.
  const submitFlow = useCallback(async () => {
    if (!hasAnyJob) return;
    if (!canWrite) { say('You do not have Write permission for Result Entry.', 'warning'); return; }
    if (await validateSubmit()) return;
    const ok = await confirm({ title: 'Submit', message: `Submit the ${selRows.size} selected result${selRows.size === 1 ? '' : 's'}? This advances the workflow.`, tone: 'default' });
    if (!ok) return;
    try {
      const byOid = new Map<string, typeof selectedRows>();
      for (const r of selectedRows) {
        const oid = r.jobOid ?? job?.jobOid ?? '';
        if (!oid) continue;
        if (!byOid.has(oid)) byOid.set(oid, []);
        byOid.get(oid)!.push(r);
      }
      let totalSubmitted = 0; let lastStatus = '';
      for (const [oid, jobRows] of byOid.entries()) {
        const payload = { rows: jobRows.map(r => ({
          id: r.id, numericResult: r.numericResult, result: r.result, df: r.df,
          qualifier: r.qualifier, comment: r.comment, analyzedBy: r.analyzedBy, analyzedDate: r.analyzedDate,
        })) };
        const res = await api.post(`/api/v1/result-entry/${oid}/submit`, payload);
        const d = res.data as { submittedRows: number; requested: number; newStatus: string };
        totalSubmitted += d.submittedRows; lastStatus = d.newStatus;
      }
      setDirty(false);
      say(`Submitted successfully. ${totalSubmitted} result${totalSubmitted === 1 ? '' : 's'} advanced to ${lastStatus}.`, 'success');
      onBack();
    } catch (e: unknown) {
      say(e instanceof Error ? e.message : 'Submit failed.', 'error');
    }
  }, [hasAnyJob, job, jobs, confirm, validateSubmit, selectedRows, selRows.size, say, onBack, canWrite]);

  const steps: IslandStep[] = [
    { id: 'select', label: 'Select Job/Batch', state: 'done' },
    { id: 'load',   label: 'Load Samples',      state: 'done' },
    { id: 'enter',  label: 'Enter Results',     state: resultsDone ? 'done' : 'active' },
    { id: 'save',   label: 'Save',              state: savedOnce ? 'done' : (resultsDone ? 'active' : 'todo') },
    { id: 'submit', label: 'Submit',            state: savedOnce ? 'active' : 'todo' },
  ];

  // Expose save/submit to parent (used when the hintbar buttons live in the page).
  useImperativeHandle(ref, () => ({
    save:   () => void saveFlow(),
    submit: () => void submitFlow(),
  }), [saveFlow, submitFlow]);

  // In inline mode, report step progress + button-enabled state to the parent.
  useEffect(() => {
    if (!inline || !onStepChange) return;
    const canSave   = !loading && rows.length > 0 && selRows.size > 0 && canWrite;
    const canSubmit = !loading && rows.length > 0 && selRows.size > 0
      && (savedOnce || selectedAlreadyPendingSubmission) && canWrite;
    onStepChange({ resultsDone, savedOnce, canSave, canSubmit });
  }, [inline, onStepChange, resultsDone, savedOnce, selectedAlreadyPendingSubmission, loading, rows.length, selRows.size, canWrite]);

  // In inline mode, publish the active grid's export payload UP so the page's top
  // control bar can render the "Export to" button between Refresh and Query (user
  // request 2026-09-05). Recomputes with the visible rows / active tab columns.
  useEffect(() => {
    if (!inline || !onExportData) return;
    onExportData({ filename: `result-entry-${job?.jobID ?? 'export'}`, ...gridExportMatrix(filtered, cols) });
  }, [inline, onExportData, job?.jobID, filtered, cols]);

  // Sync the load time up to the parent FormTitle.
  useEffect(() => {
    if (inline && onLoadMs) onLoadMs(loadMs);
  }, [inline, onLoadMs, loadMs]);

  // ── Module action keys (CLAUDE.md standing rule 14, §18.6, ADR-0013) — each
  //    def MIRRORS this detail's own action button (same handler + disabled gate),
  //    grouped under the form title.  Delete lives in the shared toolbar shown in
  //    BOTH modes, so it's always bound.  Save / Submit are top-level buttons ONLY
  //    in FULL-PAGE mode; in INLINE mode the host page (ResultEntryExpressPage)
  //    binds them via detailRef, so they're excluded here to avoid a duplicate
  //    registration.  Called unconditionally (the ARRAY is gated) before the first
  //    early return so hook order never changes.
  const reeKeys: HotkeyDef[] = [
    { combo: 'alt+d', label: 'Delete', group: 'Result Entry Express', order: 3,
      run: () => void deleteSelected(), disabled: selRows.size === 0 || !canDelete },
    // Comment + Project Notes ride in the shared toolbar shown in BOTH modes, so
    // they're always bound (mirrors their buttons; no disabled gate).
    { combo: 'alt+m', label: 'Comment', group: 'Result Entry Express', order: 4,
      run: () => setCommentOpen(true) },
    { combo: 'alt+j', label: 'Project Notes', group: 'Result Entry Express', order: 5,
      run: () => setNotesOpen(true) },
  ];
  if (!inline) {
    reeKeys.push(
      { combo: 'mod+s', label: 'Save', group: 'Result Entry Express', order: 1,
        run: () => void saveFlow(),
        disabled: loading || rows.length === 0 || selRows.size === 0 || !canWrite },
      // Alt+U Submit — the documented workflow key (Keyboard Shortcuts Manual v3 §4:
      // "sUbmit"); Ctrl+Enter stays a working alias (hidden), so both fire submitFlow.
      { combo: 'alt+u', label: 'Submit', group: 'Result Entry Express', order: 2,
        run: () => void submitFlow(),
        disabled: loading || rows.length === 0 || selRows.size === 0
          || (!savedOnce && !selectedAlreadyPendingSubmission) || !canWrite },
      { combo: 'mod+enter', label: 'Submit', group: 'Result Entry Express', order: 2, hidden: true,
        run: () => void submitFlow(),
        disabled: loading || rows.length === 0 || selRows.size === 0
          || (!savedOnce && !selectedAlreadyPendingSubmission) || !canWrite },
    );
  }
  useHotkeys(reeKeys);

  // ── Shared fragments (reused in both inline and full-page returns) ─────────

  // Toolbar controls — search + action buttons. Used directly inside the inline
  // save row (merged into one strip) and wrapped in ree-toolbar-row for full-page.
  const toolbarActionsJsx = (
    <>
      <div className="list-search" style={{ width: 260, flexShrink: 0 }}>
        <span className="tb-i">🔍</span>
        <input type="text" placeholder="Search sample, test, parameter…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
      </div>
      <button className="ca-btn" onClick={() => setCommentOpen(true)}
        title={`Add a comment note for this job. (${formatCombo('alt+m')})`}>
        <i className="ti ti-message-circle" style={{ color: 'var(--ai-icon-info, #2f7fd1)' }} /> Comment
      </button>
      {canDelete && (
        <button className="ca-btn" onClick={() => void deleteSelected()} disabled={selRows.size === 0}
          title={(selRows.size === 0 ? 'Select rows to delete.' : 'Delete the selected rows.') + ` (${formatCombo('alt+d')})`}>
          <i className="ti ti-trash" style={{ color: 'var(--ai-icon-danger, #c0392b)' }} /> Delete
        </button>
      )}
      <button className="ca-btn" onClick={() => setNotesOpen(true)} title={`Project notes for this job (${formatCombo('alt+j')})`}>
        <i className="ti ti-notes" style={{ color: 'var(--ai-icon-audit)' }} /> Project Notes ({notesCount})
      </button>
      {/* Export to — INLINE mode moves it UP to the page top control bar (user
          request 2026-09-05, between Refresh and Query), published via onExportData.
          FULL-PAGE mode still renders it here in its own toolbar row (below). */}
      {!inline && (
        <AppToolbar
          exportData={{ filename: `result-entry-${job?.jobID ?? 'export'}`, ...gridExportMatrix(filtered, cols) }}
        />
      )}
    </>
  );

  // Inner body — tab bar + (toolbar for full-page only) + grid.
  const detailBodyJsx = (
    <>
      {/* ── Tab bar — Sample · QC Sample · Surrogate · Internal Standard ── */}
      <div className="ree-tab-bar" role="tablist">
        {RESULT_TABS.map(m => {
          const active = selMode === m;
          return (
            <button key={m} type="button" role="tab" aria-selected={active}
              className={`ree-tab${active ? ' ree-tab-active' : ''}`}
              onClick={() => setSelMode(m)}>
              {m}
            </button>
          );
        })}
      </div>

      {/* Toolbar — full-page mode only; inline mode merges it into the save row */}
      {!inline && (
        <div className="ree-toolbar-row">
          {toolbarActionsJsx}
        </div>
      )}

      {error ? (
        <div className="bl-err" style={{ margin: 12 }}>
          <i className="ti ti-alert-circle" /> {error}
          <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
        </div>
      ) : (
        <AppDataGrid<SampleRow>
          className={RESULT_GRID_NO_ZEBRA}
          rows={filtered}
          columns={cols}
          getRowId={r => r.id}
          /* Own .ree-maximize-btn + host Export — no duplicate grid cluster
             (user report 2026-09-23). */
          hideHeaderTools
          stickyColumns={2}
          /* Permit-limit row tint — the lab's rules from Settings ▸ Permit Row Colors (permitRowClassFor). */
          getRowClassName={r => permitRowClassFor(r.permitLimits, r.result, r.numericResult, r.units, r.mdl)}
          selectable
          selectedIds={selRows}
          onSelectionChange={onSelectionChange}
          onCellCommit={commit}
          onCellChange={canWrite ? stageCell : undefined}
          loading={loading}
          loadMs={loadMs}
          emptyText={job
            ? 'No pending-entry samples for this job'
            : 'Set your criteria in the Query Panel above and click Load to view samples'}
        />
      )}
    </>
  );

  const gridAreaJsx = (
    <>
      {!inline && (
        <StepHint tone={resultsDone ? 'complete' : 'guidance'}>
          {resultsDone
            ? <><b>All results entered.</b> Tick the rows to save, click <b>Save</b>, then tick + <b>Submit</b> to advance them for validation.</>
            : <><b>Step 3 · Enter Results.</b> Click a <b>NumericResult</b> (or <b>Result</b> for presence/absence) cell to edit in place — required-yet-empty cells are tinted. <b>Right-click</b> a cell → <b>Copy To All Cell</b>. Ticking a row stamps <b>AnalyzedBy/Date</b>; <b>Save</b> / <b>Submit</b> act on the ticked rows only — Submit additionally requires Result, Analyzed Date, and Analyzed By.</>}
        </StepHint>
      )}
      <div className="bl-card bl-card-flex">
        {detailBodyJsx}
      </div>
    </>
  );

  const popupsJsx = !job ? null : (
    <>
      {notesOpen && (
        <NotesListPopup
          batchId={job.jobOid}
          businessObject="Result Entry"
          batchTest="Result Entry"
          jobOid={job.jobOid}
          batchLabel={job.jobID}
          readOnly={!canWrite}
          onClose={() => setNotesOpen(false)}
          onChanged={() => { /* count auto-refreshes via useNotesCount */ }}
        />
      )}
      {ctxMenu && (
        <div style={{ position: 'fixed', left: Math.min(ctxMenu.x, window.innerWidth - 210),
            top: Math.min(ctxMenu.y, window.innerHeight - 60), zIndex: 4000, minWidth: 170,
            background: 'var(--ai-card, #fff)', border: '1px solid var(--ai-line)', borderRadius: 6,
            boxShadow: '0 6px 24px rgba(0,0,0,.18)', fontSize: 12, padding: '4px 0' }}
          onClick={e => e.stopPropagation()}>
          <div onClick={copyToAll} style={{ padding: '7px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--ai-row-hover, #f1f3f7)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <i className="ti ti-copy" style={{ color: 'var(--ai-icon-ok, #1f9159)' }} /> Copy To All Cell
          </div>
        </div>
      )}
      {commentOpen && (
        <NotesListPopup
          batchId={job.jobOid}
          businessObject="Comment"
          batchTest="Comment"
          jobOid={job.jobOid}
          batchLabel={job.jobID}
          readOnly={!canWrite}
          onClose={() => setCommentOpen(false)}
          onChanged={() => { /* count auto-refreshes via useNotesCount */ }}
        />
      )}
      {openTrend && (
        <TrendChartPopup
          route={openTrend.route}
          batchOid={openTrend.batchOid}
          sampleLabel={openTrend.label}
          method={openTrend.method}
          permitLimit={null}
          permitWarn={null}
          onClose={() => setOpenTrend(null)}
        />
      )}
    </>
  );

  // ── INLINE mode (parent page owns page wrapper, hintbar, TopbarStepper) ────
  if (inline) {
    const canSave   = hasAnyJob && !loading && rows.length > 0 && selRows.size > 0 && canWrite;
    const canSubmit = hasAnyJob && !loading && rows.length > 0 && selRows.size > 0
      && (savedOnce || selectedAlreadyPendingSubmission) && canWrite;
    return (
      <div className={`ree-detail-inline${maximized ? ' ree-detail-maximized' : ''}`}>
        {/* Single panel card: Save/Submit strip at top, then tabs+toolbar+grid */}
        <div className="bl-card bl-card-flex ree-inline-panel">
          {/* Single combined strip: Search + actions on the left, Save/Submit on the right */}
          <div className="ree-inline-save-row">
            {toolbarActionsJsx}
            {dirty && <span className="ree-inline-unsaved">● Unsaved changes</span>}
            {!canWrite && <span className="ree-inline-ro"><i className="ti ti-lock" />&nbsp;Read-only</span>}
            <div className="ree-inline-btns">
              {/* Save / Submit moved UP to the page control bar (user request
                  2026-09-05, order Clear · Load · Save · Submit). They render HERE —
                  next to the maximize button — ONLY when the grid is MAXIMIZED, since
                  the full-screen overlay hides the control bar; minimizing returns
                  them to the control bar. The page drives them via detailRef.save()/
                  submit() and the canSave/canSubmit published by onStepChange. */}
              {maximized && canWrite && (
                <>
                  <button className="ca-btn ca-btn-solid" onClick={() => void saveFlow()}
                    disabled={!canSave}
                    title={(!canWrite ? 'You do not have Write permission.'
                      : selRows.size === 0 ? 'Select rows to save.'
                      : `Save the ${selRows.size} selected result${selRows.size === 1 ? '' : 's'}.`) + ` (${formatCombo('mod+s')})`}>
                    <i className="ti ti-device-floppy" />&nbsp;Save
                  </button>
                  <button className="ca-btn ca-btn-solid" onClick={() => void submitFlow()}
                    disabled={!canSubmit}
                    title={(!canWrite ? 'You do not have Write permission.'
                      : selRows.size === 0 ? 'Select rows to submit.'
                      : !savedOnce && !selectedAlreadyPendingSubmission ? 'Save first, then Submit.'
                      : `Submit the ${selRows.size} selected result${selRows.size === 1 ? '' : 's'} for validation.`) + ` (${formatCombo('mod+enter')})`}>
                    <i className="ti ti-send" />&nbsp;Submit
                  </button>
                </>
              )}
              <button type="button" className="ree-maximize-btn"
                onClick={() => setMaximized(m => !m)}
                title={maximized ? 'Restore grid to inline view.' : 'Expand grid to full page.'}
                aria-label={maximized ? 'Restore grid' : 'Maximize grid'}>
                <i className={`ti ${maximized ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} aria-hidden="true" />
              </button>
            </div>
          </div>
          {detailBodyJsx}
        </div>
        {popupsJsx}
      </div>
    );
  }

  // ── FULL PAGE mode (direct navigation / stand-alone route) ─────────────────
  if (!job) return null;   // full-page is only reached with a real job
  return (
    <div className="bl-page">
      <TopbarStepper steps={steps} />
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span style={{ cursor: 'pointer' }} onClick={() => void guardedExit(onBack)}>Result Entry Express</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{job.jobID}</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="resexpress" title={`Result Entry Express — ${job.jobID}`}
              auditFormName="Result Entry" auditSourceId={job.jobOid} loadMs={loadMs} loading={loading} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="ca-btn" onClick={() => void guardedExit(onBack)}><span aria-hidden="true">←</span>&nbsp;Back</button>
              <button className="ca-btn ca-btn-solid" onClick={() => void saveFlow()}
                disabled={loading || rows.length === 0 || selRows.size === 0 || !canWrite}
                title={(!canWrite ? 'You do not have Write permission for Result Entry.'
                  : selRows.size === 0 ? 'Select the rows to save.' : `Save the ${selRows.size} selected result${selRows.size === 1 ? '' : 's'}.`) + ` (${formatCombo('mod+s')})`}>
                💾&nbsp;Save</button>
              <button className="ca-btn ca-btn-solid" onClick={() => void submitFlow()}
                disabled={loading || rows.length === 0 || selRows.size === 0
                  || (!savedOnce && !selectedAlreadyPendingSubmission) || !canWrite}
                title={(!canWrite ? 'You do not have Write permission for Result Entry.'
                  : selRows.size === 0 ? 'Select the rows to submit.'
                  : !savedOnce && !selectedAlreadyPendingSubmission ? 'Save first, then Submit.'
                  : `Submit the ${selRows.size} selected result${selRows.size === 1 ? '' : 's'} for validation.`) + ` (${formatCombo('mod+enter')})`}>
                ⬆️&nbsp;Submit</button>
            </div>
          </div>
          <div className="dp-subtitle">
            {job.client ?? ''} · {loading ? '…' : rows.length} rows · {pendingCount} awaiting a result
            {dirty && <span style={{ color: 'var(--ai-amber)', marginLeft: 10 }}>● Unsaved changes</span>}
            {!loading && !canWrite && (
              <span style={{ color: 'var(--ai-muted)', marginLeft: 10 }}>
                <i className="ti ti-lock" />&nbsp;Read-only — you don't have Write permission for Result Entry.
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        {gridAreaJsx}
      </div>
      {popupsJsx}
    </div>
  );
});

export default ResultEntryDetail;
